import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Trash2, ChevronLeft, ChevronRight, Save, Download } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PhotoLightbox({ photos, startIndex = 0, onClose, onChanged }) {
  const [idx, setIdx] = useState(startIndex);
  const [caption, setCaption] = useState(photos[startIndex]?.caption || "");
  const [savingState, setSavingState] = useState("idle");
  const timerRef = useRef(null);

  useEffect(() => {
    setCaption(photos[idx]?.caption || "");
    setSavingState("idle");
  }, [idx, photos]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIdx((i) => Math.min(photos.length - 1, i + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photos.length, onClose]);

  if (!photos.length) return null;
  const photo = photos[idx];

  const scheduleSave = (next) => {
    setCaption(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    setSavingState("saving");
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.patch(`/photos/${photo.photo_id}`, { caption: next });
        setSavingState("saved");
        onChanged?.(data?.report);
        setTimeout(() => setSavingState("idle"), 800);
      } catch {
        setSavingState("idle");
        toast.error("Kuvatekstin tallennus epäonnistui");
      }
    }, 600);
  };

  const remove = async () => {
    if (!confirm("Poistetaanko kuva pysyvästi?")) return;
    try {
      const { data } = await api.delete(`/photos/${photo.photo_id}`);
      onChanged?.(data?.report);
      if (photos.length <= 1) onClose();
      else setIdx((i) => Math.max(0, i - 1));
      toast.success("Kuva poistettu");
    } catch {
      toast.error("Poisto epäonnistui");
    }
  };

  const download = async () => {
    try {
      const url = `${BACKEND}/api/photos/${photo.photo_id}`;
      const resp = await fetch(url);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      const safe = (caption || photo.photo_id).replace(/[^a-z0-9_\-äöå]/gi, "_").slice(0, 60) || photo.photo_id;
      a.download = `${safe}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Lataus epäonnistui");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex flex-col" data-testid="photo-lightbox" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex items-center justify-between px-4 md:px-6 h-14 text-white">
        <div className="text-sm font-semibold">{idx + 1} / {photos.length}</div>
        <div className="flex items-center gap-2">
          <button onClick={download} className="px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm inline-flex items-center gap-1.5" data-testid="lightbox-download">
            <Download className="h-4 w-4" /> Lataa
          </button>
          <button onClick={remove} className="px-3 py-1.5 rounded-lg bg-red-500/90 hover:bg-red-600 text-white text-sm inline-flex items-center gap-1.5" data-testid="lightbox-delete">
            <Trash2 className="h-4 w-4" /> Poista
          </button>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-white" data-testid="lightbox-close">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-2 md:px-12 relative min-h-0">
        {idx > 0 && (
          <button onClick={() => setIdx((i) => i - 1)} className="absolute left-2 md:left-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white" data-testid="lightbox-prev">
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        <img
          src={`${BACKEND}/api/photos/${photo.photo_id}`}
          alt={photo.caption || ""}
          className="max-h-full max-w-full object-contain rounded-lg"
          data-testid="lightbox-image"
        />
        {idx < photos.length - 1 && (
          <button onClick={() => setIdx((i) => i + 1)} className="absolute right-2 md:right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white" data-testid="lightbox-next">
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>

      <div className="bg-black/70 backdrop-blur p-4 md:p-5">
        <div className="max-w-3xl mx-auto">
          <label className="block text-xs uppercase tracking-wider text-white/60 font-semibold mb-1.5">
            Kuvateksti (näkyy raportissa kuvan alla)
          </label>
          <div className="relative">
            <textarea
              rows={2}
              value={caption}
              onChange={(e) => scheduleSave(e.target.value)}
              placeholder="Kirjoita kuvateksti..."
              className="w-full bg-white/95 text-slate-900 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-400"
              data-testid="lightbox-caption"
            />
            <div className="absolute top-2 right-3 text-[10px] uppercase tracking-wider font-semibold pointer-events-none">
              {savingState === "saving" && <span className="text-amber-600">Tallennetaan…</span>}
              {savingState === "saved" && <span className="text-emerald-700 inline-flex items-center gap-1"><Save className="h-3 w-3" /> Tallennettu</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
